import { type Component, markRaw, readonly, ref } from 'vue';

type ModalProps = Record<string, unknown>;

type ModalOptions = {
	closableOnBackdrop?: boolean;
	closableOnEsc?: boolean;
};

type OpenModalParams = {
	props?: ModalProps;
	options?: ModalOptions;
};

export type ModalInstance = {
	id: number;
	component: Component;
	props: ModalProps;
	options: Required<ModalOptions>;
};

const DEFAULT_OPTIONS: Required<ModalOptions> = {
	closableOnBackdrop: true,
	closableOnEsc: true,
};

const modalStack = ref<ModalInstance[]>([]);
let modalIdCounter = 0;

function openModal(component: Component, params: OpenModalParams = {}): number {
	const nextId = ++modalIdCounter;

	modalStack.value.push({
		id: nextId,
		component: markRaw(component),
		props: params.props ?? {},
		options: {
			...DEFAULT_OPTIONS,
			...params.options,
		},
	});

	return nextId;
}

function closeModal(id: number): void {
	modalStack.value = modalStack.value.filter((modal) => modal.id !== id);
}

function closeTopModal(): void {
	const topModal = modalStack.value.at(-1);
	if (!topModal) {
		return;
	}

	closeModal(topModal.id);
}

function closeAllModals(): void {
	modalStack.value = [];
}

export function useModalManager() {
	return {
		modalStack: readonly(modalStack),
		openModal,
		closeModal,
		closeTopModal,
		closeAllModals,
	};
}
